"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer({
  isMobile = false,
  closeMobileMenu
}: {
  isMobile?: boolean;
  closeMobileMenu?: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [currentPath, setCurrentPath] = useState("");
  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);
  if (loading) {
    return <div className={cn("flex gap-4", isMobile ? "flex-col items-start w-full" : "items-center")}>
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-6 w-20 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  if (isMobile) {
    return <nav className="flex flex-col space-y-6 w-full items-center">
        {menuItems.map((item, i) => {
        const isActive = currentPath === item.href || currentPath === "/" && item.href === "/HomePage";
        return <div key={item.id} data-index={i} className="w-full text-center">
              <Link to={item.href || "#"} className={cn("text-lg font-semibold block py-2", isActive ? "text-primary" : "")}
          // Erzwingt: nicht aktiv = weiß (gegen globale CSS)
          style={{
            color: isActive ? undefined : "#ffffff"
          }} onClick={closeMobileMenu}>
                {item.label}
              </Link>
            </div>;
      })}
      </nav>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => {
        const isActive = currentPath === item.href || currentPath === "/" && item.href === "/HomePage";
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className={cn("relative text-sm font-semibold transition-colors py-2 group", isActive ? "text-primary" : "text-foreground/90 hover:text-primary")}>
                  {item.label}
                  <span className={cn("absolute bottom-0 bg-primary transition-all duration-300 ease-out h-[2px]", isActive ? "w-full left-0" : "w-0 left-1/2 group-hover:w-full group-hover:left-0")} />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Body-Scroll sperren, wenn Menü offen ist
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    if (isMobileMenuOpen) {
      html.style.overflow = "hidden";
      body.style.overflow = "hidden";
      body.style.touchAction = "none";
    } else {
      html.style.overflow = "";
      body.style.overflow = "";
      body.style.touchAction = "";
    }
    return () => {
      html.style.overflow = "";
      body.style.overflow = "";
      body.style.touchAction = "";
    };
  }, [isMobileMenuOpen]);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;
      if (href === "/#") {
        e.preventDefault();
        window.scrollTo({
          top: 0,
          behavior: "smooth"
        });
        setIsMobileMenuOpen(false);
        return;
      }
      let hash = "";
      if (href.startsWith("#")) {
        hash = href;
      } else if (href.startsWith("/") && href.includes("#")) {
        const [path, hashPart] = href.split("#");
        if (path === "/" || path === window.location.pathname) {
          hash = "#" + hashPart;
        }
      }
      if (hash && hash !== "#") {
        const element = document.querySelector(hash);
        if (element) {
          e.preventDefault();
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
          setIsMobileMenuOpen(false);
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <>
      <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 dark bg-background text-foreground font-sans", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-white/10 py-3 shadow-lg shadow-black/20" : "bg-background py-5 border-b border-transparent")}>
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-between relative z-50">
          <Link to="/" className="flex-shrink-0 z-50">
            <img src="https://just-shepherd.10web.cloud/wp-content/uploads/2026/02/medienschmiede_ai_logo_white_transparent_tight-scaled.png" alt="AIMediaForge Logo" className="h-7 w-auto object-contain" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <MenuProvider menu_id="38" className="hidden md:block">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          <div className="hidden md:flex items-center">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_-3px_var(--primary)] hover:shadow-[0_0_25px_-5px_var(--primary)] transition-all duration-300 font-semibold">
              <Link to="/ContactPage">Kontaktiere uns</Link>
            </Button>
          </div>

          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary hover:bg-transparent z-50" onClick={() => setIsMobileMenuOpen(v => !v)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </header>

      {/* Overlay direkt nach dem Header (ohne Portal) */}
      <div className={cn("fixed inset-0 bg-black/90 text-white backdrop-blur-xl z-[9999] md:hidden transition-all duration-300", isMobileMenuOpen ? "opacity-100 visible pointer-events-auto" : "opacity-0 invisible pointer-events-none")}>
        {/* Close-Button im Overlay */}
        <button type="button" aria-label="Close menu" className="absolute top-4 right-4 z-[10000] inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 hover:bg-white/15 active:bg-white/20" onClick={() => setIsMobileMenuOpen(false)}>
          <X className="h-6 w-6 text-white" />
        </button>

        <div className="h-full w-full flex items-center justify-center pt-16">
          <div className="w-full max-w-xs space-y-8 text-center px-4">
            <MenuProvider menu_id="38">
              <HeaderMenuConsumer isMobile closeMobileMenu={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>

            <div className="pt-6 border-t border-border/50 w-full flex justify-center">
              <Button asChild size="lg" className="w-full max-w-[200px] bg-primary text-primary-foreground shadow-[0_0_20px_-5px_var(--primary)]">
                <Link to="/ContactPage" onClick={() => setIsMobileMenuOpen(false)} className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive h-10 rounded-md px-6 has-[>svg]:px-4 w-full max-w-[200px] bg-primary shadow-[0_0_20px_-5px_var(--primary)] !text-[hsl(0,0%,0%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90">Kontaktiere uns</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>;
}